// Admin page for configurable Stage-4 analysis prompts.
// List by section → edit a prompt → Save (new version) · switch active version · Revert
// to original. Placeholders ({{name}}) are shown so editors keep the runtime tokens.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import AppShell from "../components/AppShell";
import Toast from "../components/Toast";
import "../components/toast.css";
import {
  promptsList, promptGet, promptSaveVersion, promptActivate, promptRevert,
} from "../services/api";

export default function PromptsAdminPage({ currentUser }) {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState(null);
  const [detail, setDetail] = useState(null);       // full prompt (with versions)
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const showToast = (message, type = "info") => setToast({ message, type });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const d = await promptsList();
      setPrompts(d.prompts || []);
    } catch (e) { showToast(e?.message || "Failed to load prompts", "error"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const openPrompt = useCallback(async (key) => {
    setActiveKey(key);
    setDetail(null);
    try {
      const d = await promptGet(key);
      setDetail(d);
      setDraft(d.active_content || d.original || "");
    } catch (e) { showToast(e?.message || "Failed to load prompt", "error"); }
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const p of prompts) (g[p.section] = g[p.section] || []).push(p);
    return g;
  }, [prompts]);

  const save = async () => {
    if (!detail || !draft.trim()) return;
    setBusy(true);
    try {
      await promptSaveVersion(detail.key, draft, null);
      showToast("Saved as a new version - now active", "success");
      await Promise.all([reload(), openPrompt(detail.key)]);
    } catch (e) { showToast(e?.message || "Save failed", "error"); }
    finally { setBusy(false); }
  };

  const activate = async (versionNo) => {
    setBusy(true);
    try {
      await promptActivate(detail.key, versionNo);
      await Promise.all([reload(), openPrompt(detail.key)]);
      showToast(versionNo === 0 ? "Reverted to original" : `Activated v${versionNo}`, "success");
    } catch (e) { showToast(e?.message || "Activate failed", "error"); }
    finally { setBusy(false); }
  };

  const revert = async () => {
    setBusy(true);
    try {
      await promptRevert(detail.key);
      await Promise.all([reload(), openPrompt(detail.key)]);
      showToast("Reverted to code original", "success");
    } catch (e) { showToast(e?.message || "Revert failed", "error"); }
    finally { setBusy(false); }
  };

  const dirty = detail && draft !== (detail.active_content || detail.original || "");

  return (
    <AppShell currentUser={currentUser}>
      <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
        <button type="button" onClick={() => navigate("/analyses")}
          style={{ border: "1px solid var(--border)", background: "#fff", color: "#334155",
            borderRadius: 9, padding: "6px 12px", cursor: "pointer", fontSize: 13, fontWeight: 700,
            marginBottom: 14 }}>
          ← Back to Analyses
        </button>
        <h1 style={{ fontSize: 22, fontWeight: 900, margin: "0 0 4px", color: "#0f172a" }}>Analysis prompts</h1>
        <div style={{ color: "#64748b", fontSize: 13, marginBottom: 18 }}>
          Every Stage-4 (report generation) prompt. Editing saves a new version that's reused
          from then on; you can switch active versions or revert to the code original. Keep the
          <code style={{ background: "#f1f5f9", padding: "1px 5px", borderRadius: 5, margin: "0 4px" }}>{"{{placeholder}}"}</code>
          tokens - they're filled at runtime.
        </div>

        {loading ? (
          <div style={{ color: "#64748b" }}>Loading…</div>
        ) : (
          <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
            {/* Left: list by section */}
            <div style={{ width: 340, flexShrink: 0 }}>
              {Object.keys(grouped).sort().map((section) => (
                <div key={section} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                    letterSpacing: "0.06em", color: "#94a3b8", margin: "0 0 6px" }}>{section}</div>
                  {grouped[section].map((p) => (
                    <button key={p.key} type="button" onClick={() => openPrompt(p.key)}
                      style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px",
                        borderRadius: 8, border: "1px solid var(--border)", marginBottom: 5, cursor: "pointer",
                        background: activeKey === p.key ? "#eef2ff" : "#fff", color: "#0f172a",
                        fontWeight: activeKey === p.key ? 800 : 600, fontSize: 13 }}>
                      <span>{p.label}</span>
                      {p.is_overridden && (
                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: "#b45309",
                          background: "#fef3c7", padding: "1px 6px", borderRadius: 9 }}>
                          v{p.active_version_no}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              ))}
            </div>

            {/* Right: editor */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {!detail ? (
                <div style={{ color: "#94a3b8", fontSize: 14, padding: 40, textAlign: "center",
                  border: "1px dashed var(--border)", borderRadius: 12 }}>
                  Select a prompt to edit.
                </div>
              ) : (
                <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 18, background: "#fff" }}>
                  <div style={{ fontWeight: 900, fontSize: 16, color: "#0f172a" }}>{detail.label}</div>
                  <div style={{ fontSize: 12.5, color: "#64748b", margin: "2px 0 10px" }}>{detail.description}</div>
                  <div style={{ fontSize: 12, color: "#475569", marginBottom: 10 }}>
                    Placeholders:{" "}
                    {(detail.placeholders || []).map((ph) => (
                      <code key={ph} style={{ background: "#f1f5f9", padding: "1px 6px", borderRadius: 5, marginRight: 5 }}>
                        {`{{${ph}}}`}
                      </code>
                    ))}
                    {(!detail.placeholders || detail.placeholders.length === 0) && <em>none</em>}
                  </div>
                  <textarea value={draft} onChange={(e) => setDraft(e.target.value)} spellCheck={false}
                    style={{ width: "100%", minHeight: 360, fontFamily: "ui-monospace, Menlo, monospace",
                      fontSize: 12.5, lineHeight: 1.5, padding: 12, borderRadius: 10,
                      border: "1px solid var(--border)", boxSizing: "border-box", resize: "vertical",
                      color: "#0f172a", background: "#fff" }} />
                  <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
                    <button type="button" onClick={save} disabled={busy || !dirty} className="btn-primary"
                      style={{ padding: "8px 16px", borderRadius: 9, fontWeight: 800, fontSize: 13,
                        opacity: (busy || !dirty) ? 0.55 : 1 }}>
                      {busy ? "Saving…" : "Save as new version"}
                    </button>
                    <button type="button" onClick={() => setDraft(detail.original || "")}
                      disabled={busy} style={{ border: "1px solid var(--border)", background: "#fff",
                        color: "#334155", borderRadius: 9, padding: "8px 12px", cursor: "pointer", fontSize: 13 }}>
                      Load original into editor
                    </button>
                    {detail.active_version_no > 0 && (
                      <button type="button" onClick={revert} disabled={busy}
                        style={{ border: "none", background: "transparent", color: "#b91c1c",
                          cursor: "pointer", fontSize: 12.5, fontWeight: 700 }}>
                        Revert to original
                      </button>
                    )}
                    <span style={{ fontSize: 12, color: "#64748b" }}>
                      Active: {detail.active_version_no === 0 ? "original" : `v${detail.active_version_no}`}
                    </span>
                  </div>

                  {/* Version history */}
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                      letterSpacing: "0.06em", color: "#94a3b8", marginBottom: 6 }}>Versions</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      <button type="button" onClick={() => activate(0)} disabled={busy}
                        style={{ padding: "4px 10px", borderRadius: 8, cursor: "pointer", fontSize: 12,
                          border: "1px solid var(--border)", color: "#0f172a",
                          background: detail.active_version_no === 0 ? "#dcfce7" : "#fff",
                          fontWeight: detail.active_version_no === 0 ? 800 : 600 }}>
                        original
                      </button>
                      {(detail.versions || []).map((v) => (
                        <button key={v.version_no} type="button" onClick={() => activate(v.version_no)} disabled={busy}
                          title={v.created_at || ""}
                          style={{ padding: "4px 10px", borderRadius: 8, cursor: "pointer", fontSize: 12,
                            border: "1px solid var(--border)", color: "#0f172a",
                            background: v.is_active ? "#dcfce7" : "#fff",
                            fontWeight: v.is_active ? 800 : 600 }}>
                          v{v.version_no}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />
      </div>
    </AppShell>
  );
}
